// src/pages/Inscription/InscriptionPage.jsx
import React, { useState, useEffect } from "react";
import useScrollToTop from "./../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import { ToastContainer } from "react-toastify";
import InfoBubble from "./../../components/InfoBubble/InfoBubble";
import InscriptionForm from "./InscriptionForm";
import InscriptionFiche from "./InscriptionFiche";
import { getActeursByUser } from "./../../services/userServices";
import { getActeurById } from "./../../services/acteursServices";
import { useUser } from "./../../contexts/UserContext";
import PageHeader from "../../components/Layout/PageHeader";


const InscriptionPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userId = user?.userId;

  const [acteurs, setActeurs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchActeurs = async () => {
    if (!userId) {
      setActeurs([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const acteursData = await getActeursByUser(userId);
      const detailedActeurs = await Promise.all(
        (acteursData || []).map(async (acteur) => {
          const acteurDetails = await getActeurById(acteur.id);
          return acteurDetails;
        })
      );
      setActeurs(detailedActeurs);
    } catch (error) {
      console.error("Erreur lors de la récupération des acteurs :", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActeurs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return (
    <div className="min-h-screen pt-[140px] pb-10 bg-slate-50 dark:bg-slate-900 dark:text-white">
      <ScrollIndicator />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <PageHeader
          eyebrow="Utilisateur connecté"
          title="Inscription & fiche acteur du judo"
          subtitle="Déclarez les acteurs du Judo de votre club (enseignants, dirigeants, arbitres, commissaires sportifs, membres de commission, bénévoles) afin que le comité puisse leur attribuer les bons rôles et accès."
          align="center"
          size="lg"
        />

        {/* InfoBubble introductif */}
        <div className="mb-6">
          <InfoBubble title="Pourquoi créer une fiche acteur ?">
            <p className="text-sm">
              Chaque fiche acteur permet au comité d’identifier clairement les
              personnes ressources du département : responsables de club,
              enseignants, arbitres, commissaires sportifs, membres du comité ou
              des commissions. À partir de ces informations, l’administrateur
              pourra vous octroyer un rôle adapté avec des accès supplémentaires
              sur l’application (convocations, documents, outils spécifiques…).
            </p>
          </InfoBubble>
        </div>

        {/* Grille principale : Fiches à gauche / Formulaire à droite */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-start">
          {/* Colonne gauche : acteurs existants */}
          <section className="space-y-4">
            <div className="rounded-2xl bg-white/90 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 shadow-lg p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-sm font-semibold tracking-[0.18em] uppercase text-primary">
                    Vos fiches acteurs
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Liste des acteurs déjà déclarés avec leurs groupes.
                  </p>
                </div>
                <span className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-[11px] text-slate-600 dark:text-slate-300">
                  {loading ? "Chargement..." : `${acteurs.length} acteur(s)`}
                </span>
              </div>

              <InscriptionFiche
                acteurs={acteurs}
                fetchActeurs={fetchActeurs}
                loading={loading}
              />
            </div>
          </section>

          {/* Colonne droite : formulaire */}
          <section>
            <InscriptionForm onActorCreated={fetchActeurs} />
          </section>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default InscriptionPage;
